import React, { useEffect, useRef, useState } from "react";
import { connect } from "react-redux";
import * as maptilersdk from '@maptiler/sdk'
import { polygonCreateRequest } from "../../../../action/polygon-actions";
import { collapseLeftToolbar } from "../../../../action/left-toolbar-state-actions";
import { MEASUREMENT_TYPES } from '../../../../lib/enums'
import toaster from '../../../main-ui/toast'

const POLY_SRC = 'drawing-polygon-src'
const POLY_FILL = 'drawing-polygon-fill'
const POLY_LINE = 'drawing-polygon-line'

function haversineMeters([lng1, lat1], [lng2, lat2]) {
  const R = 6371000
  const dLat = (lat2 - lat1) * Math.PI / 180
  const dLng = (lng2 - lng1) * Math.PI / 180
  const a = Math.sin(dLat / 2) ** 2 + Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * Math.sin(dLng / 2) ** 2
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))
}
function segmentsLength(coords) {
  let total = 0
  for (let i = 1; i < coords.length; i++) total += haversineMeters(coords[i - 1], coords[i])
  return total
}
function polyRingArea(coords) {
  const R = 6371000
  let area = 0
  const n = coords.length - 1
  for (let i = 0; i < n; i++) {
    const [lng1, lat1] = coords[i]
    const [lng2, lat2] = coords[(i + 1) % n]
    area += (lng2 - lng1) * Math.PI / 180 *
      (2 + Math.sin(lat1 * Math.PI / 180) + Math.sin(lat2 * Math.PI / 180))
  }
  return Math.abs(area * R * R / 2)
}
function formatDist(m) {
  return m >= 1000 ? (m / 1000).toFixed(2) + ' km' : Math.round(m) + ' m'
}
function formatArea(sqm) {
  return sqm >= 10000 ? (sqm * 0.0001).toFixed(2) + ' ha' : Math.round(sqm) + ' m²'
}

const ThreeDPlygonDetails = (props) => {
  const [color, setColor] = useState('#0000FF')
  const [name, setName] = useState(`Polygon - ${props.polygons.length + 1}`)
  const [notes, setnotes] = useState('')
  const [loading, setLoading] = useState(false)

  const drawing = useRef(false)
  const activeCoords = useRef([])
  const markerRef = useRef(null)
  const colorRef = useRef(color)
  const tooltipElRef = useRef(null)
  const tooltipPopupRef = useRef(null)

  useEffect(() => { colorRef.current = color }, [color])

  useEffect(() => {
    const map = props.mapReferenceThreed
    if (!map) return

    const handleClick = (e) => {
      const { lat, lng } = e.lngLat
      const newPoint = { lat, lng }

      if (!drawing.current) {
        drawing.current = true
        activeCoords.current = [newPoint]

        const el = document.createElement('div')
        el.innerHTML = `<svg viewBox="0 0 12 12" width="14" height="14" xmlns="http://www.w3.org/2000/svg">
          <circle cx="6" cy="6" r="5" fill="${colorRef.current}" stroke="white" stroke-width="1.5"/>
        </svg>`
        markerRef.current = new maptilersdk.Marker({ element: el, anchor: 'center', pitchAlignment: 'viewport', rotationAlignment: 'viewport' })
          .setLngLat([lng, lat])
          .addTo(map)

        const tipEl = document.createElement('div')
        tipEl.style.cssText = 'background:rgba(0,0,0,0.8);color:#fff;padding:5px 9px;border-radius:5px;font-size:12px;pointer-events:none;white-space:nowrap;line-height:1.6'
        tipEl.innerHTML = 'Perimeter: 0 m<br>Area: 0 m²'
        tooltipElRef.current = tipEl
        tooltipPopupRef.current = new maptilersdk.Popup({ closeButton: false, offset: 12, anchor: 'bottom' })
          .setDOMContent(tipEl).setLngLat([lng, lat]).addTo(map)
      } else {
        activeCoords.current.push(newPoint)
        if (markerRef.current) markerRef.current.setLngLat([lng, lat])
        if (tooltipElRef.current) {
          const ring = [...activeCoords.current.map(p => [p.lng, p.lat])]
          ring.push(ring[0])
          tooltipElRef.current.innerHTML = `Perimeter: ${formatDist(segmentsLength(ring))}<br>Area: ${formatArea(polyRingArea(ring))}`
          tooltipPopupRef.current?.setLngLat([lng, lat])
        }
      }

      updatePreview(map)
    }

    const handleDblClick = (e) => {
      e.preventDefault()
      drawing.current = false
      if (tooltipPopupRef.current) { tooltipPopupRef.current.remove(); tooltipPopupRef.current = null }
      tooltipElRef.current = null
    }

    const addPreviewLayers = () => {
      if (!map.getSource(POLY_SRC)) {
        map.addSource(POLY_SRC, {
          type: 'geojson',
          data: { type: 'Feature', geometry: { type: 'Polygon', coordinates: [[]] }, properties: {} }
        })
        map.addLayer({
          id: POLY_FILL,
          type: 'fill',
          source: POLY_SRC,
          paint: { 'fill-color': colorRef.current, 'fill-opacity': 0.2 }
        })
        map.addLayer({
          id: POLY_LINE,
          type: 'line',
          source: POLY_SRC,
          layout: { 'line-join': 'round', 'line-cap': 'round' },
          paint: { 'line-color': colorRef.current, 'line-width': 2, 'line-dasharray': [2, 1] }
        })
      }
    }

    try { addPreviewLayers() } catch (_) {}
    map.on('styledata', addPreviewLayers)

    const handleMouseMove = (e) => {
      if (!drawing.current || activeCoords.current.length === 0) return
      const { lat, lng } = e.lngLat
      const src = map.getSource(POLY_SRC)
      if (!src) return
      const ring = [...activeCoords.current.map(p => [p.lng, p.lat]), [lng, lat]]
      ring.push(ring[0])
      src.setData({ type: 'Feature', geometry: { type: 'Polygon', coordinates: [ring] }, properties: {} })
      if (tooltipElRef.current) {
        tooltipElRef.current.innerHTML = `Perimeter: ${formatDist(segmentsLength(ring))}<br>Area: ${formatArea(polyRingArea(ring))}`
        tooltipPopupRef.current?.setLngLat([lng, lat])
      }
    }

    map.on('click', handleClick)
    map.on('dblclick', handleDblClick)
    map.on('mousemove', handleMouseMove)
    map.getCanvas().style.cursor = 'crosshair'

    return () => {
      map.off('click', handleClick)
      map.off('dblclick', handleDblClick)
      map.off('mousemove', handleMouseMove)
      map.off('styledata', addPreviewLayers)
      map.getCanvas().style.cursor = ''
      try {
        if (map.getLayer(POLY_FILL)) map.removeLayer(POLY_FILL)
        if (map.getLayer(POLY_LINE)) map.removeLayer(POLY_LINE)
        if (map.getSource(POLY_SRC)) map.removeSource(POLY_SRC)
      } catch (_) {}
      if (markerRef.current) { markerRef.current.remove(); markerRef.current = null }
      if (tooltipPopupRef.current) { tooltipPopupRef.current.remove(); tooltipPopupRef.current = null }
      tooltipElRef.current = null
      drawing.current = false
      activeCoords.current = []
    }
  }, [props.mapReferenceThreed])

  const updatePreview = (map) => {
    const src = map.getSource(POLY_SRC)
    if (!src || activeCoords.current.length < 2) return
    const ring = [...activeCoords.current.map(p => [p.lng, p.lat])]
    ring.push(ring[0])
    src.setData({ type: 'Feature', geometry: { type: 'Polygon', coordinates: [ring] }, properties: {} })
  }

  const undoThePoints = () => {
    if (!activeCoords.current || activeCoords.current.length <= 1) return
    activeCoords.current.pop()
    const last = activeCoords.current[activeCoords.current.length - 1]
    if (last && markerRef.current) markerRef.current.setLngLat([last.lng, last.lat])
    const map = props.mapReferenceThreed
    if (map) updatePreview(map)
    if (tooltipElRef.current && activeCoords.current.length > 0) {
      const ring = [...activeCoords.current.map(p => [p.lng, p.lat])]
      ring.push(ring[0])
      tooltipElRef.current.innerHTML = `Perimeter: ${formatDist(segmentsLength(ring))}<br>Area: ${formatArea(polyRingArea(ring))}`
    }
    if (activeCoords.current.length === 0) {
      drawing.current = false
      if (tooltipPopupRef.current) { tooltipPopupRef.current.remove(); tooltipPopupRef.current = null }
      tooltipElRef.current = null
    }
  }

  const onCreatePolygon = () => {
    if (activeCoords.current.length < 3) return
    const ring = [...activeCoords.current.map(p => [p.lng, p.lat])]
    ring.push(ring[0])
    const geoJson = JSON.stringify({
      type: 'FeatureCollection',
      features: [{
        type: 'Feature',
        geometry: { type: 'Polygon', coordinates: [ring] },
        properties: { color, fillColor: color }
      }]
    })
    const payload = { name, notes, userId: props.user.id, geoJson, measurementTypeId: MEASUREMENT_TYPES.POLYGON, public: false, measureType: 'polygon' }
    setLoading(true)
    props.polygonCreateRequest(payload)
      .then(() => { setLoading(false); props.collapseLeftToolbar() })
      .catch(err => {
        // Keep the panel open so the polygon can be saved again (WEB-7164)
        console.error('Error saving polygon:', err)
        setLoading(false)
        toaster.baseMapError('Couldn\'t save your polygon. Please try again.')
      })
  }

  function GetColorSelection() {
    const colors = ['#0000FF', '#90FF00', '#589500', '#FFFF00', '#FF6800', '#FF0000']
    return (
      <div>
        <div>POLYGON & FILL COLOR</div>
        <div style={{ display: 'flex', marginTop: '10px', marginBottom: '10px' }}>
          {colors.map((c) => {
            const active = color.toUpperCase() === c.toUpperCase()
            return (
              <div
                key={`shape-color-${c}`}
                onClick={() => {
                  setColor(c)
                  colorRef.current = c
                  const map = props.mapReferenceThreed
                  if (map) {
                    if (map.getLayer(POLY_FILL)) map.setPaintProperty(POLY_FILL, 'fill-color', c)
                    if (map.getLayer(POLY_LINE)) map.setPaintProperty(POLY_LINE, 'line-color', c)
                  }
                  if (markerRef.current) {
                    const circle = markerRef.current.getElement().querySelector('circle')
                    if (circle) circle.setAttribute('fill', c)
                  }
                }}
                className="color-option"
                style={{ backgroundColor: c, border: active ? '4px solid #c1c0c0' : '', cursor: 'pointer' }}
              >
                {active ? <div className="color-active"></div> : null}
              </div>
            )
          })}
        </div>
      </div>
    )
  }

  return (
    <div className='tools-content'>
      {loading && <div className='markers-load-spinner'><div className="loader-spinner"></div></div>}
      <div className='left-expanded-header-container'>
        <span className='left-expanded-header'>Add Polygon</span>
        <div className='default-close left-dialog-close' onClick={() => props.collapseLeftToolbar()}></div>
      </div>

      <div>
        <label htmlFor='name' style={{ fontSize: '12px', textTransform: 'capitalize' }}>Name : </label>
        <input type='text' name='name' id='name' className='default-name-textfield'
          style={{ marginTop: '5px' }} placeholder='Name' value={name}
          onChange={(e) => setName(e.target.value)} />
      </div>

      <div style={{ marginTop: 20, marginBottom: 20 }}>
        <GetColorSelection />
      </div>

      <div style={{ marginTop: '10px', marginBottom: '10px' }}>
        <label htmlFor='notes' style={{ fontSize: '12px' }}>Notes:
          <textarea name='shapeNotes' id='shapeNotes' className='default-name-textfield'
            value={notes} onChange={(e) => setnotes(e.target.value)} placeholder=' '
            style={{ height: 100, padding: 10 }} />
        </label>
      </div>

      <div style={{ width: '100%', display: 'flex', flexDirection: 'row', justifyContent: 'space-between' }}>
        <button style={{ width: '32%', margin: '0', backgroundColor: 'grey' }}
          onClick={() => props.collapseLeftToolbar()}>Cancel</button>
        <button style={{ width: '32%', margin: '0', backgroundColor: 'orange' }}
          onClick={undoThePoints}>Undo</button>
        <button id='save-button' onClick={onCreatePolygon}
          style={{ width: '32%', margin: '0', backgroundColor: '#589500', cursor: 'pointer' }}>
          Save Changes
        </button>
      </div>
    </div>
  )
}

const mapStateToProps = (state) => ({
  mapReferenceThreed: state.mapReferenceThreed,
  user: state.user,
  polygons: state.polygons,
})

const mapDispatchToProps = (dispatch) => ({
  polygonCreateRequest: (payload) => dispatch(polygonCreateRequest(payload)),
  collapseLeftToolbar: () => dispatch(collapseLeftToolbar())
})

export default connect(mapStateToProps, mapDispatchToProps)(ThreeDPlygonDetails)
